// The issue table (Console D, views/triage.js), shared by the issues list and the release view's
// top issues: status as a glyph and a word (D100), kind as a coloured word, the issue as a symbol
// (mono, never translated) with its message under it, sessions, users, a 14-day sparkline
// (APS-298), the release it was first seen in, when it was last seen and the assignee. The list
// adds a checkbox per row, sortable heads (APS-299) and a keyboard cursor: rows are focusable,
// one at a time in the tab order (a roving tabindex), and the cursor row is the view's accent.
import { Link } from '@tanstack/react-router'
import type { IssueSummary } from '../api/types'
import { useI18n } from '../i18n'
import { initials } from '../lib/format'
import { glyphOf, type IssueGlyph, issueTitle } from '../lib/issues'
import type { IssueSortSearch } from '../routes/search'
import { Kind, ScrollX, Spark, Status, type StatusKey } from './d'

const GLYPH_STATUS: Record<IssueGlyph, StatusKey> = {
  new: 'new',
  ongoing: 'on',
  regressed: 'reg',
  resolved: 'res',
  ignored: 'mute',
}

/** The status as a glyph and its word, so it never relies on the glyph (or color) alone. */
export function StatusGlyph({
  glyph,
  release,
}: {
  glyph: IssueGlyph
  release?: string | null | undefined
}) {
  const { t } = useI18n()
  const word =
    glyph === 'new'
      ? t('issue.status.new')
      : glyph === 'ongoing'
        ? t('issue.status.ongoing')
        : glyph === 'resolved'
          ? t('issues.state.resolved')
          : glyph === 'ignored'
            ? t('issues.state.ignored')
            : release
              ? t('issues.state.regressedIn', { release })
              : t('issues.state.regressed')
  return <Status s={GLYPH_STATUS[glyph]} label={word.toLowerCase()} />
}

/** The issue's kind as a coloured word (telemetry: crash, hang, anr, error, abnormal_exit). */
export function KindPill({ kind }: { kind: string }) {
  return <Kind k={kind} />
}

/** Row selection, from the issues list (not in the sandbox, which reads only). */
export interface IssueSelection {
  selected: ReadonlySet<string>
  toggle: (id: string) => void
  /** Selects every row shown, or none. */
  toggleAll: (on: boolean) => void
}

/** The keyboard cursor: the focused row's index (-1 before any). */
export interface IssueCursor {
  index: number
  set: (index: number) => void
}

/** The list's sort, read from and written to the URL. */
export interface IssueSorting {
  sort: IssueSortSearch | undefined
  asc: boolean
  set: (sort: IssueSortSearch) => void
}

export function IssueTable({
  issues,
  previousIds,
  orgId,
  projectId,
  app,
  release,
  caption,
  selection,
  cursor,
  assignees,
  sorting,
}: {
  issues: IssueSummary[]
  /** Issue ids of the previous release, for "new" where the first release is not known. */
  previousIds?: Set<string> | undefined
  orgId: string
  projectId: string
  app?: string | undefined
  /** The release "new" is measured in. */
  release?: string | undefined
  caption: string
  selection?: IssueSelection | undefined
  cursor?: IssueCursor | undefined
  /** Members' names by user id (empty while loading): shows the Assignee column. */
  assignees?: ReadonlyMap<string, string> | undefined
  sorting?: IssueSorting | undefined
}) {
  const { t, number, relative } = useI18n()
  const all = selection
    ? issues.length > 0 && issues.every((i) => selection.selected.has(i.id))
    : false
  const roving = cursor ? Math.max(0, Math.min(cursor.index, issues.length - 1)) : -1
  const spark = issues.some((i) => i.daily)
  const head = (key: IssueSortSearch | null, label: string, className = '') => {
    if (!sorting || !key)
      return (
        <th scope="col" className={className || undefined}>
          {label}
        </th>
      )
    const on = (sorting.sort ?? 'users') === key
    return (
      <th
        scope="col"
        className={['sort', on ? 'on' : '', className].filter(Boolean).join(' ')}
        aria-sort={on ? (sorting.asc ? 'ascending' : 'descending') : 'none'}
      >
        <button type="button" onClick={() => sorting.set(key)}>
          {label}
        </button>
      </th>
    )
  }
  return (
    <ScrollX label={caption}>
      <table className="t is-list">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {selection ? (
              <th scope="col" className="ck">
                <input
                  type="checkbox"
                  checked={all}
                  aria-label={t('issues.state.selectAll')}
                  onChange={(e) => selection.toggleAll(e.target.checked)}
                />
              </th>
            ) : null}
            {head('status', t('issues.col.status'), selection ? '' : 'f')}
            {head('kind', t('issues.col.kind'), 'is-kc')}
            {head(null, t('issues.col.issue'))}
            {head('sessions', t('issues.col.sessions'), 'num')}
            {head('users', t('issues.col.users'), 'num')}
            {spark ? (
              <th scope="col" title={t('issues.col.trendTitle')}>
                {t('issues.col.trend')}
              </th>
            ) : null}
            {head('first_seen', t('issues.col.firstSeen'))}
            {head('last_seen', t('issues.col.lastSeen'), assignees ? '' : 'last')}
            {assignees ? (
              <th scope="col" className="last">
                {t('issues.state.col.assignee')}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {issues.map((issue, index) => {
            const selected = selection?.selected.has(issue.id) ?? false
            const assignee = issue.state?.assignee_id
            const name = assignee ? assignees?.get(assignee) : undefined
            const glyph = glyphOf(issue, previousIds, release)
            return (
              <tr
                key={issue.id}
                data-issue={issue.id}
                {...(cursor
                  ? {
                      tabIndex: index === roving ? 0 : -1,
                      className: index === cursor.index ? 'hov cur' : 'hov',
                      onFocus: () => cursor.set(index),
                    }
                  : {})}
                {...(selection ? { 'aria-selected': selected } : {})}
              >
                {selection ? (
                  <td className="ck">
                    <input
                      type="checkbox"
                      checked={selected}
                      aria-label={t('issues.state.select', { issue: issueTitle(issue) })}
                      onChange={() => selection.toggle(issue.id)}
                    />
                  </td>
                ) : null}
                <td className={selection ? undefined : 'f'}>
                  <StatusGlyph glyph={glyph} release={issue.state?.regressed_release} />
                  <span className="is-km">
                    <KindPill kind={issue.kind} />
                  </span>
                </td>
                <td className="is-kc">
                  <KindPill kind={issue.kind} />
                </td>
                <td className="is-is">
                  <Link
                    to="/o/$orgId/p/$projectId/issues/$issueId"
                    params={{ orgId, projectId, issueId: issue.id }}
                    search={app ? { app } : {}}
                    className="ttl"
                    translate="no"
                  >
                    {issueTitle(issue)}
                  </Link>
                  <span className="t2">
                    {[
                      issue.message && issue.message !== issue.type ? (
                        <span key="m" translate="no">
                          {issue.message}
                        </span>
                      ) : null,
                      !issue.symbolicated && issue.kind !== 'abnormal_exit' ? (
                        <span key="s" className="c-warn">
                          {t('issue.notSymbolicated')}
                        </span>
                      ) : null,
                      issue.releases.length ? (
                        <span key="r" translate="no">
                          {issue.releases.slice(-2).join(', ')}
                        </span>
                      ) : null,
                    ]
                      .filter(Boolean)
                      .flatMap((part, n) => (n ? [' · ', part] : [part]))}
                  </span>
                </td>
                <td className="num">{number(issue.sessions)}</td>
                <td className="num">{number(issue.users)}</td>
                {spark ? (
                  <td>{issue.daily ? <Spark values={issue.daily.sessions.slice(-14)} /> : null}</td>
                ) : null}
                <td translate="no">{issue.first_seen_release || relative(issue.first_seen)}</td>
                <td className={assignees ? undefined : 'last'}>{relative(issue.last_seen)}</td>
                {assignees ? (
                  <td className="last">
                    {!assignee ? (
                      <>
                        <span aria-hidden="true" className="faint">
                          –
                        </span>
                        <span className="visually-hidden">{t('issues.state.unassigned')}</span>
                      </>
                    ) : name ? (
                      <span className="is-who" translate="no">
                        <span className="avatar" aria-hidden="true">
                          {initials(name, '')}
                        </span>
                        {name}
                      </span>
                    ) : assignees.size > 0 ? (
                      // Members are loaded and this one is not among them any more.
                      t('issues.state.formerMember')
                    ) : null}
                  </td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </ScrollX>
  )
}
